/**
 * Small pure helpers shared by every deal-document upload form (create panel, existing-deal
 * dialog): stable keys for a picked file, and turning an upload failure into one readable
 * `<file name>: <reason>` line. The accepted-format list itself lives in @protoss/shared
 * (DEAL_DOCUMENT_*); nothing here decides what a file is.
 */

import { ApiError } from '../../../lib/apiClient';

/** One note shown on a picked file after an upload attempt. */
export interface FileNote {
  kind: 'error' | 'warning';
  text: string;
}

/** Key of a device file in a per-file notes map. */
export function localFileKey(file: { name: string; size: number }): string {
  return `local:${file.name}:${file.size}`;
}

/** Key of a Drive-picked file in a per-file notes map. */
export function driveFileKey(file: { id: string }): string {
  return `drive:${file.id}`;
}

const SERVER_CODE_REASONS: Record<string, string> = {
  UNSUPPORTED_DOCUMENT_TYPE:
    'this file type is not supported. Use PDF, Word, text, .vtt/.srt, .eml, .pptx, JSON, YAML, XML or .html.',
  FILE_TOO_LARGE: 'this file is too large.',
  FILE_REQUIRED: 'no file was received.',
  EMPTY_FILE_UPLOAD: 'this file is empty.',
  GOOGLE_DRIVE_EXPORT_TOO_LARGE: 'this Google file is too large for Drive to export.',
  GOOGLE_DRIVE_FILE_NOT_ACCESSIBLE: 'coSow cannot open this Drive file.',
  GOOGLE_DRIVE_FILE_NOT_FOUND: 'this Drive file was not found.',
};

/** The reason an upload failed, in plain words (server codes mapped, server sentences kept). */
export function uploadFailureReason(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err ?? '');
  const code = SERVER_CODE_REASONS[message.trim()];
  if (code) return code;
  return message.trim() || 'upload failed.';
}

/**
 * `<file name>: <reason>`. The server's own sentences usually already quote the file name, in
 * which case the reason is used as is instead of repeating it.
 */
export function formatFileIssue(fileName: string, reason: string): string {
  return reason.includes(fileName) ? reason : `${fileName}: ${reason}`;
}

/**
 * True when the server rejected THIS file (unsupported / unreadable / too large / a Drive file it
 * cannot open) as opposed to a problem with the request as a whole (auth, missing company name,
 * server down). Only a file-level failure lets the create flow move on to the next file as the
 * deal's seed.
 */
export function isFileLevelUploadError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.statusCode === 413 || err.statusCode === 422) return true;
  if (err.statusCode === 400) {
    return ['UNSUPPORTED_DOCUMENT_TYPE', 'EMPTY_FILE_UPLOAD', 'FILE_TOO_LARGE'].includes(
      err.message.trim()
    );
  }
  return (
    (err.statusCode === 403 || err.statusCode === 404) && err.message.startsWith('GOOGLE_DRIVE_')
  );
}

/** Wrap pasted text into the same kind of File a picked file would be. */
export function pastedTextToFile(text: string, existingNames: readonly string[]): File {
  let name = 'pasted-text.txt';
  for (let n = 2; existingNames.includes(name); n += 1) name = `pasted-text-${n}.txt`;
  return new File([text], name, { type: 'text/plain' });
}

/**
 * Shown when the optional "Additional notes" could not be saved on the deal. The analysis still gets
 * the notes (they travel in the run), so this is a heads-up, not a blocker.
 */
export const NOTES_NOT_SAVED_MESSAGE =
  'Your additional notes could not be saved on the deal. They will still be used for this analysis.';
